import React, { useState } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Icon,
  Badge,
  Flex,
  IconButton,
  useBreakpointValue,
  Link,
  Divider,
} from '@chakra-ui/react';
import {
  SearchIcon,
  HamburgerIcon,
  InfoIcon,
  ArrowForwardIcon,
  ArrowBackIcon,
  ExternalLinkIcon,
} from '@chakra-ui/icons';
import {
  getDocumentationStructure,
  getDocBreadcrumbs,
  getAdjacentSections,
  searchDocumentation,
  DocSection,
  DocCategory,
} from '../utils/docsStructure';

interface DocumentationLayoutProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
}

const DocumentationLayout: React.FC<DocumentationLayoutProps> = ({
  children,
  title,
  description,
}) => {
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<DocSection[]>([]);
  
  // Color mode values
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const sidebarBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textSecondary = useColorModeValue('gray.600', 'gray.400');
  const linkHoverBg = useColorModeValue('gray.100', 'gray.700');
  
  // Responsive values
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const sidebarWidth = '280px';
  
  const docStructure = getDocumentationStructure();
  const breadcrumbs = getDocBreadcrumbs(location.pathname);
  const adjacentSections = getAdjacentSections(location.pathname);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.trim()) {
      const results = searchDocumentation(query);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  const SidebarNavigation: React.FC = () => (
    <VStack spacing={4} align="stretch">
      {/* Search */}
      <Box>
        <InputGroup size="sm">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search documentation..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            bg={useColorModeValue('gray.50', 'gray.700')}
            border="none"
          />
        </InputGroup>
        
        {searchResults.length > 0 && (
          <Box mt={2} p={2} bg={useColorModeValue('gray.50', 'gray.700')} borderRadius="md">
            <Text fontSize="xs" fontWeight="semibold" mb={2} color={textSecondary}>
              Search Results
            </Text>
            <VStack spacing={1} align="stretch">
              {searchResults.slice(0, 5).map((result) => (
                <Link
                  key={result.id}
                  as={RouterLink}
                  to={result.path}
                  fontSize="xs"
                  p={2}
                  borderRadius="sm"
                  _hover={{ bg: linkHoverBg }}
                  onClick={isMobile ? onClose : undefined}
                >
                  {result.title}
                </Link>
              ))}
            </VStack>
          </Box>
        )}
      </Box>

      {/* Navigation Categories */}
      <Accordion allowMultiple defaultIndex={[0]}>
        {docStructure.map((category) => (
          <AccordionItem key={category.id} border="none">
            <AccordionButton px={0} py={2} _hover={{ bg: 'transparent' }}>
              <Box flex="1" textAlign="left">
                <Text fontWeight="semibold" fontSize="sm">
                  {category.title}
                </Text>
                <Text fontSize="xs" color={textSecondary}>
                  {category.description}
                </Text>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={0} py={2}>
              <VStack spacing={1} align="stretch">
                {category.sections.map((section) => (
                  <Box key={section.id}>
                    <Link
                      as={RouterLink}
                      to={section.path}
                      display="block"
                      p={2}
                      borderRadius="md"
                      fontSize="sm"
                      fontWeight={location.pathname === section.path ? 'semibold' : 'normal'}
                      bg={location.pathname === section.path ? 'blue.50' : 'transparent'}
                      color={location.pathname === section.path ? 'blue.600' : 'inherit'}
                      borderLeft={location.pathname === section.path ? '3px solid' : 'none'}
                      borderColor="blue.500"
                      _hover={{ bg: linkHoverBg }}
                      onClick={isMobile ? onClose : undefined}
                    >
                      {section.title}
                    </Link>
                    
                    {section.subsections && (
                      <VStack spacing={1} align="stretch" ml={4} mt={1}>
                        {section.subsections.map((subsection) => (
                          <Link
                            key={subsection.id}
                            as={RouterLink}
                            to={subsection.path}
                            display="block"
                            p={1}
                            borderRadius="sm"
                            fontSize="xs"
                            fontWeight={location.pathname === subsection.path ? 'semibold' : 'normal'}
                            bg={location.pathname === subsection.path ? 'blue.50' : 'transparent'}
                            color={location.pathname === subsection.path ? 'blue.600' : textSecondary}
                            _hover={{ bg: linkHoverBg }}
                            onClick={isMobile ? onClose : undefined}
                          >
                            {subsection.title}
                          </Link>
                        ))}
                      </VStack>
                    )}
                  </Box>
                ))}
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
    </VStack>
  );

  return (
    <Box bg={bgColor} minHeight="100vh">
      {/* Mobile Navigation */}
      {isMobile && (
        <Box p={4} bg={sidebarBg} borderBottom="1px" borderColor={borderColor}>
          <HStack justify="space-between">
            <Heading size="md">Documentation</Heading>
            <IconButton
              aria-label="Open navigation menu"
              icon={<HamburgerIcon />}
              variant="ghost"
              onClick={onOpen}
            />
          </HStack>
        </Box>
      )}

      <Flex>
        {/* Desktop Sidebar */}
        {!isMobile && (
          <Box
            w={sidebarWidth}
            bg={sidebarBg}
            borderRight="1px"
            borderColor={borderColor}
            position="fixed"
            top={0}
            left={0}
            h="100vh"
            overflowY="auto"
            p={6}
            pt={8}
          >
            <VStack spacing={6} align="stretch">
              <Box>
                <Heading size="lg" mb={2}>Documentation</Heading>
                <Text fontSize="sm" color={textSecondary}>
                  Learn how to use Speedcode effectively
                </Text>
              </Box>
              <SidebarNavigation />
            </VStack>
          </Box>
        )}

        {/* Mobile Drawer */}
        <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader>Navigation</DrawerHeader>
            <DrawerBody>
              <SidebarNavigation />
            </DrawerBody>
          </DrawerContent>
        </Drawer>

        {/* Main Content */}
        <Box
          flex={1}
          ml={!isMobile ? sidebarWidth : 0}
          p={6}
        >
          <Container maxW="4xl">
            <VStack spacing={8} align="stretch">
              {/* Breadcrumbs */}
              <Breadcrumb>
                {breadcrumbs.map((crumb, index) => (
                  <BreadcrumbItem key={crumb.path} isCurrentPage={index === breadcrumbs.length - 1}>
                    {index === breadcrumbs.length - 1 ? (
                      <BreadcrumbLink>{crumb.title}</BreadcrumbLink>
                    ) : (
                      <BreadcrumbLink as={RouterLink} to={crumb.path} color="blue.500">
                        {crumb.title}
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                ))}
              </Breadcrumb>

              {/* Page Header */}
              {title && (
                <Box>
                  <Heading size="xl" mb={2}>{title}</Heading>
                  {description && (
                    <Text fontSize="lg" color={textSecondary}>
                      {description}
                    </Text>
                  )}
                </Box>
              )}

              {/* Content */}
              <Box>{children}</Box>

              {/* Navigation Footer */}
              {(adjacentSections.prev || adjacentSections.next) && (
                <Box mt={12}>
                  <Divider mb={6} />
                  <HStack justify="space-between">
                    <Box flex={1}>
                      {adjacentSections.prev && (
                        <Button
                          as={RouterLink}
                          to={adjacentSections.prev.path}
                          variant="ghost"
                          leftIcon={<ArrowBackIcon />}
                          size="sm"
                        >
                          <VStack align="start" spacing={0}>
                            <Text fontSize="xs" color={textSecondary}>Previous</Text>
                            <Text fontSize="sm">{adjacentSections.prev.title}</Text>
                          </VStack>
                        </Button>
                      )}
                    </Box>
                    
                    <Box flex={1} textAlign="right">
                      {adjacentSections.next && (
                        <Button
                          as={RouterLink}
                          to={adjacentSections.next.path}
                          variant="ghost"
                          rightIcon={<ArrowForwardIcon />}
                          size="sm"
                        >
                          <VStack align="end" spacing={0}>
                            <Text fontSize="xs" color={textSecondary}>Next</Text>
                            <Text fontSize="sm">{adjacentSections.next.title}</Text>
                          </VStack>
                        </Button>
                      )}
                    </Box>
                  </HStack>
                </Box>
              )}
            </VStack>
          </Container>
        </Box>
      </Flex>
    </Box>
  );
};

export default DocumentationLayout; 